import {
	combineValidators,
	composeValidators,
	createValidator,
	hasLengthGreaterThan,
	hasLengthLessThan,
	isRequired,
	isRequiredIf,
} from "revalidate";
import {
	creditCardValidator,
	emailFieldValidator,
	firstnameValidator,
	lastnameValidator,
	phoneFieldValidator,
} from "app/utils/form/fieldValidators";
import {
	customIsRequiredIf,
	isAmount,
	isCreditCardValid,
	mustAcceptTerms,
} from "app/utils/form/validators";
import validationMessages from "app/utils/form/validationMessages.js";
import { PAYMENT_METHODS, CREDIT_CARDS_TYPES } from "app/constants";
import { determineCardType } from "app/utils/creditCardUtils";
import get from "lodash/get";
import { getQuoteTotalWithDelta } from "app/pages/Booking/bookingSelectors";

export const contactFields = [
	"contact.name.gender",
	"contact.name.firstName",
	"contact.name.lastName",
	"contact.address.line1",
	"contact.address.line2",
	"contact.address.postCode",
	"contact.address.city",
	"contact.address.country",
	"contact.address.phone",
	"contact.email",
	"contact.phone",
];

export const paymentFields = [
	"payment.type",
	"payment.mode",
	"payment.issuerId",
	"payment.card.maidenName",
	"payment.card.birthdate.day",
	"payment.card.birthdate.month",
	"payment.card.birthdate.year",
	"payment.card.zipCodeOfBirth",
	"payment.card.encrypted",
	"payment.card.holderName",
	"payment.card.expiryMonth",
	"payment.card.expiryYear",
	"payment.card.cvc",
	"payment.card.number",
	"payment.card.amount",
	"payment.cardBCMC.maidenName",
	"payment.cardBCMC.birthdate.day",
	"payment.cardBCMC.birthdate.month",
	"payment.cardBCMC.birthdate.year",
	"payment.cardBCMC.zipCodeOfBirth",
	"payment.cardBCMC.encrypted",
	"payment.cardBCMC.holderName",
	"payment.cardBCMC.expiryMonth",
	"payment.cardBCMC.expiryYear",
	"payment.cardBCMC.cvc",
	"payment.cardBCMC.number",
	"payment.swissBilling.birthdate.day",
	"payment.swissBilling.birthdate.month",
	"payment.swissBilling.birthdate.year",
	"payment.swissBilling.invoiceType",
];

export const dependantPaymentCardFields = ["expiryMonth", "expiryYear", "cvc", "number"];

export const passengerFields = [
	"passengers[].type",
	"passengers[].index",
	"passengers[].name.gender",
	"passengers[].name.firstName",
	"passengers[].name.lastName",
	"passengers[].birthdate.day",
	"passengers[].birthdate.month",
	"passengers[].birthdate.year",
	"passengers[].frequentFlyerTravellerCardNumber",
	// Passport retiré de l apage de payment pour le moment
	// "passengers[].passport.passportNumber",
	// "passengers[].passport.countryIssue",
	// "passengers[].passport.expirationMonth",
	// "passengers[].passport.expirationYear",
];

export const insuranceField = ["insurance"];

export const termsField = ["terms"];

export const couponsFields = ["coupon"];

export const fields = [].concat(
	contactFields,
	paymentFields,
	passengerFields,
	couponsFields,
	insuranceField,
	termsField,
	"uniqueNamePassengers"
);

export const validate = (values, state) => {
	const paymentTypes = state.payment.paymentTypes;
	const selectedPaymentTypeValue = state.booking.paymentType;
	const selectedPaymentModeValue = state.booking.paymentMode;

	const selectedPaymentType = paymentTypes.find(paymentType => {
		return (
			paymentType.paymentType === selectedPaymentTypeValue &&
			paymentType.terms === selectedPaymentModeValue
		);
	});

	const acceptedCreditCardTypes =
		selectedPaymentType && selectedPaymentType.acceptedCreditCardTypes;

	let paymentValidators = {};
	if (selectedPaymentTypeValue === PAYMENT_METHODS.CASINO) {
		paymentValidators = {
			card: {
				maidenName: customIsRequiredIf({
					message: validationMessages.creditcardMaidenNameRequired,
				}),
				birthdate: {
					day: customIsRequiredIf({
						message: validationMessages.birthdateDayRequired,
					}),
					month: customIsRequiredIf({
						message: validationMessages.birthdateMonthRequired,
					}),
					year: customIsRequiredIf({
						message: validationMessages.birthdateYearRequired,
					}),
				},
				zipCodeOfBirth: composeValidators(
					isRequired({
						message: validationMessages.creditcardZipCodeOfBirthRequired,
					}),
					hasLengthGreaterThan(1)({
						message: validationMessages.departementCodeMinLength,
					})
				)("departement number"),
				holderName: isRequired({
					message: validationMessages.creditcardHolderNameRequired,
				}),
				expiryMonth: composeValidators(
					isRequired({
						message: validationMessages.creditcardExpiryMonthRequired,
					}),
					isCreditCardValid({ isOnNumberChange: false })({
						message: validationMessages.creditCardInvalid,
					})
				)("expiryMonth"),
				expiryYear: composeValidators(
					isRequired({
						message: validationMessages.creditcardExpiryYearRequired,
					}),
					isCreditCardValid({ isOnNumberChange: false })({
						message: validationMessages.creditCardInvalid,
					})
				)("expiryYear"),
				cvc: composeValidators(
					isRequired({
						message: validationMessages.creditcardCvcRequired,
					}),
					isCreditCardValid({ isOnNumberChange: false })({
						message: validationMessages.creditCardInvalid,
					})
				)("cvc"),
				number: creditCardValidator(acceptedCreditCardTypes),
			},
		};
	} else if (selectedPaymentTypeValue === PAYMENT_METHODS.CARD) {
		paymentValidators = {
			card: {
				amount: isAmount({
					message: validationMessages.creditcardAmountFormat,
				}),
				holderName: isRequired({
					message: validationMessages.creditcardHolderNameRequired,
				}),
				expiryMonth: composeValidators(
					isRequired({
						message: validationMessages.creditcardExpiryMonthRequired,
					}),
					isCreditCardValid({ isOnNumberChange: false })({
						message: validationMessages.creditCardInvalid,
					})
				)("expiryMonth"),
				expiryYear: composeValidators(
					isRequired({
						message: validationMessages.creditcardExpiryYearRequired,
					}),
					isCreditCardValid({ isOnNumberChange: false })({
						message: validationMessages.creditCardInvalid,
					})
				)("expiryYear"),
				cvc: composeValidators(
					isRequiredIf(
						() =>
							determineCardType(get(values, "payment.card.number")) !==
							CREDIT_CARDS_TYPES.MAESTRO
					)({
						message: validationMessages.creditcardCvcRequired,
					}),
					isCreditCardValid({ isOnNumberChange: false })({
						message: validationMessages.creditCardInvalid,
					})
				)("cvc"),
				number: creditCardValidator(acceptedCreditCardTypes),
			},
		};
	} else if (selectedPaymentTypeValue === PAYMENT_METHODS.BCMC) {
		paymentValidators = {
			cardBCMC: {
				holderName: isRequired({
					message: validationMessages.creditcardHolderNameRequired,
				}),
				expiryMonth: composeValidators(
					isRequired({
						message: validationMessages.creditcardExpiryMonthRequired,
					}),
					isCreditCardValid({ isOnNumberChange: false })({
						message: validationMessages.creditCardInvalid,
					})
				)("expiryMonth"),
				expiryYear: composeValidators(
					isRequired({
						message: validationMessages.creditcardExpiryYearRequired,
					}),
					isCreditCardValid({ isOnNumberChange: false })({
						message: validationMessages.creditCardInvalid,
					})
				)("expiryYear"),
				number: creditCardValidator(["bcmc"]),
			},
		};
	} else if (selectedPaymentTypeValue === PAYMENT_METHODS.IDEAL) {
		paymentValidators = {
			issuerId: isRequired({
				message: validationMessages.issuerIdRequired,
			}),
		};
	} else if (selectedPaymentTypeValue === PAYMENT_METHODS.SWISS_BILLING) {
		paymentValidators = {
			swissBilling: {
				birthdate: {
					day: customIsRequiredIf({
						message: validationMessages.birthdateDayRequired,
					}),
					month: customIsRequiredIf({
						message: validationMessages.birthdateMonthRequired,
					}),
					year: customIsRequiredIf({
						message: validationMessages.birthdateYearRequired,
					}),
				},
				invoiceType: isRequired({
					message: validationMessages.invoiceTypeRequired,
				}),
			},
		};
	}

	const validators = {
		contact: {
			name: {
				gender: isRequired({
					message: validationMessages.genderRequired,
				}),
				firstName: firstnameValidator,
				lastName: lastnameValidator,
			},
			address: {
				line1: isRequired({
					message: validationMessages.addressLine1Required,
				}),
				postCode: isRequired({
					message: validationMessages.addressPostCodeRequired,
				}),
				city: composeValidators(
					isRequired({
						message: validationMessages.addressCityRequired,
					}),
					hasLengthLessThan(51)({
						message: validationMessages.addressCityMaxLength,
					})
				)("city"),
				country: isRequired({
					message: validationMessages.addressCountryRequired,
				}),
			},
			email: emailFieldValidator,
			phone: phoneFieldValidator,
		},
		"passengers[]": createValidator(
			() => passenger => ({
				name: combineValidators({
					lastName: lastnameValidator,
					firstName: firstnameValidator,
					gender: isRequired({
						message: validationMessages.genderRequired,
					}),
				})({
					...passenger.name,
					passengers: values.passengers,
					index: passenger.index,
				}),

				birthdate: combineValidators({
					day: customIsRequiredIf({
						message: validationMessages.birthdateDayRequired,
					}),
					month: customIsRequiredIf({
						message: validationMessages.birthdateMonthRequired,
					}),
					year: customIsRequiredIf({
						message: validationMessages.birthdateYearRequired,
					}),
				})({ ...passenger.birthdate }),
			}),
			""
		)(values.passengers),
		insurance: isRequired({
			message: validationMessages.insuranceRequired,
		}),
		terms: composeValidators(
			mustAcceptTerms({
				message: validationMessages.termsRequired,
			})
		)("Terms"),
	};

	const cartAmount = getQuoteTotalWithDelta(state);

	if (cartAmount > 0) {
		validators.payment = paymentValidators;
	}

	return combineValidators(validators)(values);
};
